import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
  List,
  ListItem,
  Toolbar,
  Typography,
} from '@material-ui/core';
import {
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Close,
  ExpandLess,
  ExpandMore,
  LibraryBooks,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LessonContentData, ModuleData } from 'services/dataService';
import ContentPreview from '../ContentPreview';
import { useStyles } from './styles';

const LESSON_PUBLISHED = 1;

const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

export default ({ isOpen, onClose, courseId, courseName }) => {
  const { t } = useTranslation(['label', 'button', 'toastify']);
  const classes = useStyles();
  const [modules, setModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [openModules, setOpenModules] = useState([]);
  const [currentLessonId, setCurrentLessonId] = useState(null);
  const [contents, setContents] = useState([]);
  const [loadingContents, setLoadingContents] = useState(false);

  const lessons = useMemo(() => modules.flatMap((m) => m.lessons), [modules]);
  const currentIndex = lessons.findIndex((l) => l.id === currentLessonId);
  const currentLesson = lessons[currentIndex];

  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      setLoadingModules(true);
      try {
        const { data } = await ModuleData.getModuleByIdCourse(courseId);
        const published = (data?.modules || [])
          .filter((module) => module?.id && module.status)
          .sort(byOrder)
          .map((module) => ({
            ...module,
            lessons: (module.lessons || [])
              .filter((lesson) => lesson.status === LESSON_PUBLISHED)
              .sort(byOrder),
          }));

        setModules(published);
        const first = published.find((m) => m.lessons.length > 0);
        setOpenModules(first ? [first.id] : []);
        setCurrentLessonId(first ? first.lessons[0].id : null);
      } catch (err) {
        toast.error(t('toastify:commons.toast_error_api'));
      } finally {
        setLoadingModules(false);
      }
    })();
  }, [isOpen, courseId]);

  useEffect(() => {
    if (!currentLessonId) {
      setContents([]);
      return;
    }

    (async () => {
      setLoadingContents(true);
      try {
        const { data } = await LessonContentData.getLessonContent({
          id_lesson: currentLessonId,
        });
        setContents(data?.contents || []);
      } catch (err) {
        toast.error(t('toastify:commons.toast_error_api'));
      } finally {
        setLoadingContents(false);
      }
    })();
  }, [currentLessonId]);

  const toggleModule = (moduleId) =>
    setOpenModules((ids) =>
      ids.includes(moduleId)
        ? ids.filter((id) => id !== moduleId)
        : [...ids, moduleId]
    );

  const goToLesson = (lesson) => {
    if (!lesson) return;
    setCurrentLessonId(lesson.id);
    setOpenModules((ids) =>
      ids.includes(lesson.id_module) ? ids : [...ids, lesson.id_module]
    );
  };

  return (
    <Dialog fullScreen open={isOpen} onClose={onClose}>
      <AppBar className={classes.appBar}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <Close />
          </IconButton>
          <Typography variant="h6" className={classes.appBarTitle}>
            {t('button:Lesson.btn_student')}
            {courseName ? ` - ${courseName}` : ''}
          </Typography>
        </Toolbar>
      </AppBar>
      <Typography className={classes.notice}>
        {t('label:Lesson.label_student_view_notice')}
      </Typography>

      {loadingModules ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : lessons.length === 0 ? (
        <Typography color="textSecondary" className={classes.loading}>
          {t('label:Lesson.label_student_view_empty')}
        </Typography>
      ) : (
        <div className={classes.body}>
          <nav className={classes.sidebar}>
            {modules.map((module) => (
              <List key={module.id} disablePadding>
                <ListItem
                  button
                  className={classes.moduleItem}
                  onClick={() => toggleModule(module.id)}
                >
                  <LibraryBooks className={classes.icon} />
                  <span className={classes.label}>{module.title}</span>
                  {openModules.includes(module.id) ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </ListItem>
                <Collapse in={openModules.includes(module.id)} unmountOnExit>
                  {module.lessons.map((lesson) => (
                    <ListItem
                      key={lesson.id}
                      button
                      selected={lesson.id === currentLessonId}
                      className={classes.lessonItem}
                      onClick={() => goToLesson(lesson)}
                    >
                      <Bookmark className={classes.lessonIcon} />
                      <span className={classes.label}>{lesson.title}</span>
                    </ListItem>
                  ))}
                </Collapse>
              </List>
            ))}
          </nav>

          <main className={classes.main}>
            {currentLesson && (
              <Typography variant="h4" className={classes.lessonTitle}>
                {currentLesson.title}
              </Typography>
            )}
            {loadingContents ? (
              <div className={classes.loading}>
                <CircularProgress />
              </div>
            ) : (
              <ContentPreview contents={contents} />
            )}
            <div className={classes.navigation}>
              <Button
                variant="outlined"
                color="primary"
                disabled={currentIndex <= 0}
                onClick={() => goToLesson(lessons[currentIndex - 1])}
              >
                <ChevronLeft />
                {t('button:commons.btn_previous')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={currentIndex === -1 || currentIndex >= lessons.length - 1}
                onClick={() => goToLesson(lessons[currentIndex + 1])}
              >
                {t('button:commons.btn_next')}
                <ChevronRight />
              </Button>
            </div>
          </main>
        </div>
      )}
    </Dialog>
  );
};
